import { Navigate, Route, Routes } from 'react-router-dom';

import {
  Register,
  Verification,
  Login,
  ForgotPassword,
  AuthCheckMail,
  Programs,
  AddProgram,
  TasksList,
  UsersList,
  Invoices,
  Payments,
  Placements,
  AddPlacement,
  Products,
  ParticipantsTable,
  SingleParticipant,
  ListStages,
  Leads,
  SingleLead,
  ProfileSettings,
  CompanyInfo,
  Branches,
  Integration,
  BrandTheme,
  KnowledgeBasePage,
  KnowledgeBaseWorkspace,
  AgentsWorkspace,
  EditOrgInfo,
  SoonPage,
  Preferences,
  ParticipantsPlacements,
  EditParticipant,
  UserSettingsLayout,
  EditUserInfo,
  ProfileSettingsMyEmplyomentInfo,
  ProfileSettingsMyBankInfos,
  ProfileSettingsMyDocuments,
  ProfileSettingsMyTasks,
  Roles,
  DashboardLayout,
  LeadMangmentPage,
  ProgramsManagementPage,
  ConversationsOnboarding,
  ConversationsChannels,
  ConversationsPage,
  ContactsWorkspace,
  ContactDetailPage,
  MessageTemplatesPage,
  AutomationsWorkspace,
  AutomationActivityPage,
} from '@src/core/router/lazy-routes/routes';
import {
  relativePath,
  PATHS,
  PUBLIC_PATH,
  createNestedRoute,
  createRoute,
} from '@src/core/router/paths';
import GuestGuard from '@core/router/guards/guest-guard';
import { PlacementIdValidator } from '@core/router/guards/placement-id-validator';
import { SubscriptionGuard } from '@core/router/guards/subscription-guard';
import MinimalLayout from '@core/router/layouts/minimal-layout';
// import MainLayout from '@core/router/layouts/main-layout';
import { DataLoader } from '@core/router/data-loader';
import NotFound from '@shared/pages/not-found';
import { Unauthorized } from '@shared/pages/unauthorized';

import { SettingsLayout } from '@features/settings/components/settings-layout';
import { NewMainLayout } from './layouts/new-main-layout';
import { ConversationsLayout } from '@features/conversations/components/shell/conversations-layout';
import BankInfo from '@features/settings/pages/bank-info';
import EditBankInfo from '@features/settings/pages/bank-info/edit-bank-info';
import EditMainInfo from '@features/leads/pages/detail/components/edit-main-info';
import { LocationErrorBoundary } from '@src/core/router/guards/location-error-boundary';
import PlanAndSubDetails from '@src/features/settings/pages/billing/subscription/plan-and-sub-details';
import { entities } from '@shared/constants/entities';
import { HasPermission } from '@src/features/roles/guards/has-permission';
import BillingAndSubscription from '@features/settings/pages/billing';
import SingleUser from '@features/users/pages/detail';
import EditUser from '@features/users/pages/detail/components/edit-user';
import EditUserBankInfo from '@features/users/pages/detail/components/edit-bank-info';
import SinglePartner from '@src/features/users/pages/partner-detail';
import EditPartner from '@src/features/users/pages/partner-detail/components/edit-partner';
import { NotificationList } from '@src/features/notifications';
import { AuthGuard } from './guards/auth-guard';
import Universities from '@src/features/universities/pages/list/index';
import Announcements from '@src/features/announcements/pages/list/index';
import { HostCompaniesPage } from '@src/features/placements/pages/host-companies';
import LeadsFollowUps from '@src/features/leads/pages/follow-ups';
import { RemindersOverviewPage } from '@src/features/reminders/pages/overview';
import { AllRemindersPage } from '@src/features/reminders/pages/all-reminders';
import { RemindersTemplatesPage } from '@src/features/reminders/pages/templates';
import {
  AutomationDetailRedirect,
  AutomationEditRedirect,
  AutomationNewRedirect,
} from '@features/automations/pages/automation-redirects';

export const MainRoutes = () => {
  return (
    <Routes>
      {/* Default Redirect */}
      <Route
        path={PUBLIC_PATH.ROOT}
        element={<Navigate to={PUBLIC_PATH.LOGIN} />}
      />
      {/* TEMPORARY — demo only: both pricing routes render the same widget. */}
      <Route
        path={PUBLIC_PATH.PLANS_AND_PRICING}
        element={<PlanAndSubDetails />}
      />
      <Route
        path={PUBLIC_PATH.PLANS_AND_SUB_DETAILS}
        element={<PlanAndSubDetails />}
      />
      {/* Public (Guest) Routes */}
      <Route
        element={
          <LocationErrorBoundary>
            <GuestGuard>
              <MinimalLayout />
            </GuestGuard>
          </LocationErrorBoundary>
        }
      >
        <Route
          path={PUBLIC_PATH.REGISTER}
          element={<Register />}
        />
        <Route
          path={PUBLIC_PATH.VERIFICATION}
          element={<Verification />}
        />
        <Route
          path={PUBLIC_PATH.LOGIN}
          element={<Login />}
        />
        <Route
          path={PUBLIC_PATH.FORGOT_PASSWORD}
          element={<ForgotPassword />}
        />
        <Route
          path={PUBLIC_PATH.CHECK_MAIL}
          element={<AuthCheckMail />}
        />
      </Route>

      {/* Private (Authenticated) Routes */}
      <Route path='/:organizationSlug/*'>
        <Route
          element={
            <LocationErrorBoundary>
              <AuthGuard>
                <DataLoader>
                  <SubscriptionGuard>
                    <NewMainLayout />
                  </SubscriptionGuard>
                </DataLoader>
              </AuthGuard>
            </LocationErrorBoundary>
          }
        >
          <Route
            path={relativePath(PATHS.KNOWLEDGECENTER)}
            element={<SoonPage />}
          />

          <Route
            path='dashboards'
            element={<DashboardLayout />}
          >
            <Route
              index
              element={<LeadMangmentPage />}
            />
            <Route
              path='lead-management'
              element={<LeadMangmentPage />}
            />
            <Route
              path='programs-management'
              element={<ProgramsManagementPage />}
            />
            <Route
              path='accounting'
              element={<SoonPage />}
            />
            <Route
              path='employee'
              element={<SoonPage />}
            />
          </Route>
          <Route
            path={relativePath(PATHS.PROGRAMS)}
            element={
              <HasPermission
                entity={entities.program}
                action='view'
                mechanism='route'
              >
                <Programs />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.ADD_PROGRAM)}
            element={
              <HasPermission
                entity={entities.program}
                action='add'
                mechanism='route'
              >
                <AddProgram />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.EDITPROGRAM())}
            element={
              <HasPermission
                entity={entities.program}
                action='change'
                mechanism='route'
              >
                <AddProgram />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.REMINDERS)}
            element={
              <HasPermission
                entity={entities.reminderTemplate}
                action='view'
                mechanism='route'
              >
                <Navigate
                  to={createRoute(PATHS.REMINDERS_OVERVIEW)}
                  replace
                />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.REMINDERS_OVERVIEW)}
            element={
              <HasPermission
                entity={entities.reminderTemplate}
                action='view'
                mechanism='route'
              >
                <RemindersOverviewPage />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.REMINDERS_ALL)}
            element={
              <HasPermission
                entity={entities.reminderTemplate}
                action='view'
                mechanism='route'
              >
                <AllRemindersPage />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.REMINDERS_TEMPLATES)}
            element={
              <HasPermission
                entity={entities.reminderTemplate}
                action='view'
                mechanism='route'
              >
                <RemindersTemplatesPage />
              </HasPermission>
            }
          />
          <Route
            path='reminders/views'
            element={
              <Navigate
                to={createRoute(PATHS.REMINDERS_OVERVIEW)}
                replace
              />
            }
          />
          <Route
            path='reminders/views/overview'
            element={
              <Navigate
                to={createRoute(PATHS.REMINDERS_OVERVIEW)}
                replace
              />
            }
          />
          <Route
            path='reminders/views/all-reminders'
            element={
              <Navigate
                to={createRoute(PATHS.REMINDERS_ALL)}
                replace
              />
            }
          />
          <Route
            path='reminders/views/templates'
            element={
              <Navigate
                to={createRoute(PATHS.REMINDERS_TEMPLATES)}
                replace
              />
            }
          />
          <Route
            path={relativePath(PATHS.PLACEMENTS)}
            element={
              <HasPermission
                entity={entities.placement}
                action='view'
                mechanism='route'
              >
                <Placements />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.PLACEMENT())}
            element={
              <HasPermission
                entity={entities.placement}
                action='view'
                mechanism='route'
              >
                <PlacementIdValidator>
                  <Placements />
                </PlacementIdValidator>
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.CREATE_PLACEMENT)}
            element={
              <HasPermission
                entity={entities.placement}
                action='add'
                mechanism='route'
              >
                <AddPlacement />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.HOST_COMPANIES)}
            element={
              <HasPermission
                entity={entities.placementHostCompany}
                action='view'
                mechanism='route'
              >
                <HostCompaniesPage />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.EDIT_PLACEMENT())}
            element={
              <HasPermission
                entity={entities.placement}
                action='change'
                mechanism='route'
              >
                <PlacementIdValidator>
                  <AddPlacement />
                </PlacementIdValidator>
              </HasPermission>
            }
          />
          {/* <Route
            path={relativePath(PATHS.COMMISSION)}
            element={<Commission />}
          /> */}
          <Route
            path={relativePath(PATHS.TASKS)}
            element={
              <HasPermission
                action='view'
                mechanism='route'
                entity={entities.task}
              >
                <TasksList />
              </HasPermission>
            }
          />

          <Route
            path={relativePath(PATHS.USERS)}
            element={<UsersList />}
          />
          <Route
            path={relativePath(PATHS.SINGLE_USER)}
            element={<SingleUser />}
          />
          <Route
            path={relativePath(PATHS.SINGLE_PARTNER)}
            element={<SinglePartner />}
          />
          <Route
            path={relativePath(PATHS.SINGLE_PARTNER_EDIT)}
            element={<EditPartner />}
          />
          <Route
            path={relativePath(PATHS.USER_EDIt)}
            element={<EditUser />}
          />
          <Route
            path={relativePath(PATHS.BANK_EDIT)}
            element={<EditUserBankInfo />}
          />

          <Route
            path={relativePath(PATHS.ROLES)}
            element={
              <HasPermission
                entity={entities.role}
                action='view'
                mechanism='route'
              >
                <Roles />
              </HasPermission>
            }
          />

          <Route
            path={relativePath(PATHS.ROLE())}
            element={
              <HasPermission
                entity={entities.role}
                action='view'
                mechanism='route'
              >
                <Roles />
              </HasPermission>
            }
          />

          <Route
            path={relativePath(PATHS.EDIT_ROLE())}
            element={
              <HasPermission
                entity={entities.role}
                action='change'
                mechanism='route'
              >
                <Roles />
              </HasPermission>
            }
          />

          <Route
            path={relativePath(PATHS.ACCOUNTING_INVOICES)}
            element={
              <HasPermission
                action='view'
                mechanism='route'
                entity={entities.invoice}
              >
                <Invoices />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.ACCOUNTING_PAYMENTS)}
            element={
              <HasPermission
                action='view'
                mechanism='route'
                entity={entities.payment}
              >
                <Payments />
              </HasPermission>
            }
          />

          <Route
            path={relativePath(PATHS.ACCOUNTING_PRODUCTS)}
            element={
              <HasPermission
                action='view'
                mechanism='route'
                entity={entities.product}
              >
                <Products />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.PARTICIPANTS)}
            element={<ParticipantsTable />}
          />
          <Route
            path={relativePath(PATHS.ALL_PARTICIPANTS)}
            element={<ParticipantsTable allParticipants />}
          />
          <Route
            path={relativePath(PATHS.SINGLE_SUBMISSION())}
            element={<SingleParticipant />}
          />
          <Route
            path={relativePath(PATHS.EDIT_PARTICIPANT())}
            element={<EditParticipant />}
          />
          <Route
            path={relativePath(PATHS.STAGES)}
            element={
              <HasPermission
                entity={[entities.participantStageProgress, entities.stage]}
                action='view'
                mechanism='route'
              >
                <ListStages />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.PARTICIPANTS_PLACEMENTS)}
            element={
              <HasPermission
                entity={entities.placementApplication}
                action='view'
                mechanism='route'
              >
                <ParticipantsPlacements />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.LEADS)}
            element={
              <HasPermission
                entity={entities.lead}
                action='view'
                mechanism='route'
              >
                <Leads />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.LEADS_FOLLOW_UPS)}
            element={
              <HasPermission
                entity={entities.lead}
                action='view'
                mechanism='route'
              >
                <LeadsFollowUps />
              </HasPermission>
            }
          />

          <Route
            path={relativePath(PATHS.SINGLE_LEAD)}
            element={
              <HasPermission
                entity={entities.lead}
                action='view'
                mechanism='route'
              >
                <SingleLead />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.LEAD_MAIN_INFO_EDIT)}
            element={
              <HasPermission
                entity={entities.lead}
                action='change'
                mechanism='route'
              >
                <EditMainInfo />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.EDIT_ORG_INFO)}
            element={<EditOrgInfo />}
          />
          <Route
            path={relativePath(PATHS.EDIT_MY_INFO())}
            element={<EditUserInfo />}
          />

          <Route
            path={relativePath(PATHS.SETTINGS_BANK_INFO_EDIT())}
            element={
              <HasPermission
                action='change'
                mechanism='route'
                entity={[entities.bankAccount, entities.eWalletAccount]}
              >
                <EditBankInfo />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.SETTINGS_BANK_INFO_NEW)}
            element={
              <HasPermission
                action='add'
                mechanism='route'
                entity={[entities.bankAccount, entities.eWalletAccount]}
              >
                <EditBankInfo />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.SETTINGS_PROFILE_SETTINGS)}
            element={<UserSettingsLayout />}
          >
            <Route
              index
              element={<ProfileSettings />}
            />
            {/* temporarily disabled activity logs */}
            {/* <Route
              path={createNestedRoute(PATHS.SETTINGS_PROFILE_ACTIVITYLOGS, {
                depth: 2,
              })}
              element={<ProfileSettingsActivityLogs />}
            /> */}
            <Route
              path={createNestedRoute(PATHS.SETTINGS_PROFILE_TASKS, {
                depth: 2,
              })}
              element={<ProfileSettingsMyTasks />}
            />

            <Route
              path={createNestedRoute(PATHS.SETTINGS_PROFILE_DOCUMENTS, {
                depth: 2,
              })}
              element={<ProfileSettingsMyDocuments />}
            />

            <Route
              path={createNestedRoute(PATHS.SETTINGS_PROFILE_EMPLOYMENT_INFOS, {
                depth: 2,
              })}
              element={<ProfileSettingsMyEmplyomentInfo />}
            />

            <Route
              path={createNestedRoute(PATHS.SETTINGS_PROFILE_BANK_INFOS, {
                depth: 2,
              })}
              element={<ProfileSettingsMyBankInfos />}
            />
          </Route>

          <Route
            path={relativePath(PATHS.SETTINGS)}
            element={<SettingsLayout />}
          >
            <Route
              index
              element={
                <Navigate
                  to={createNestedRoute(PATHS.SETTINGS_COMPANY_INFO)}
                  replace
                />
              }
            />
            <Route
              path={createNestedRoute(PATHS.SETTINGS_COMPANY_INFO)}
              element={
                <HasPermission
                  action='view'
                  mechanism='route'
                  entity={entities.organization}
                >
                  <CompanyInfo />
                </HasPermission>
              }
            />
            <Route
              path={createNestedRoute(PATHS.SETTINGS_PREFERENCES)}
              element={<Preferences />}
            />
            <Route
              path={createNestedRoute(PATHS.SETTINGS_BANK_INFO)}
              element={
                <HasPermission
                  action='view'
                  mechanism='route'
                  entity={[entities.bankAccount, entities.eWalletAccount]}
                >
                  <BankInfo />
                </HasPermission>
              }
            />
            <Route
              path={createNestedRoute(PATHS.SETTINGS_BILLING_A_SUBSCRIPTION)}
              element={
                <HasPermission
                  action='view'
                  mechanism='route'
                  entity={entities.organizationsubscription}
                >
                  <BillingAndSubscription />
                </HasPermission>
              }
            />
            <Route
              path={createNestedRoute(PATHS.SETTINGS_BRANCHES)}
              element={<Branches />}
            />
            <Route
              path={createNestedRoute(PATHS.SETTINGS_INTEGRATION)}
              element={
                <HasPermission
                  action='view'
                  mechanism='route'
                  entity={entities.integration}
                >
                  <Integration />
                </HasPermission>
              }
            />
            <Route
              path={createNestedRoute(PATHS.SETTINGS_BRAND_THEME)}
              element={
                <HasPermission
                  action='view'
                  mechanism='route'
                  entity={entities.organization}
                >
                  <BrandTheme />
                </HasPermission>
              }
            />
            {/*
              There is no knowledge-base entity in the permission map: the
              documents the public agent quotes live alongside the inbox
              channels it answers on, so this route reuses `integration` —
              anyone allowed to wire a channel up is allowed to see what the
              agent quotes. Write access is enforced separately: the knowledge
              API only accepts changes from members with the server-derived
              `can_manage_knowledge` flag, and the page renders read-only for
              everyone else.
            */}
            {/* Retired: every document now belongs to a knowledge base and is managed
                on the knowledge base page. Kept as a redirect so old links still land
                somewhere useful. */}
            <Route
              path={createNestedRoute(PATHS.SETTINGS_KNOWLEDGE_BASE)}
              element={
                <Navigate
                  to={createRoute(PATHS.KNOWLEDGE_BASE)}
                  replace
                />
              }
            />
            {/* Retired: agents are created in the agents workspace. This page's
                form never sent a knowledge base, so every create failed. Kept as a
                redirect so old links still land somewhere useful. */}
            <Route
              path={createNestedRoute(PATHS.SETTINGS_AI_AGENTS)}
              element={
                <Navigate
                  to={createRoute(PATHS.CONVERSATIONS_AGENTS)}
                  replace
                />
              }
            />
          </Route>
          <Route
            path={relativePath(PATHS.Notifications)}
            element={<NotificationList />}
          />
          <Route
            path={relativePath(PATHS.UNIVERSITIES)}
            element={<Universities />}
          />
          <Route
            path={relativePath(PATHS.ANNOUNCEMENTS)}
            element={
              <HasPermission
                entity={entities.announcement}
                action='view'
                mechanism='route'
              >
                <Announcements />
              </HasPermission>
            }
          />
          {/*
            The knowledge bases the AI agents answer customers from. Same
            `integration` gate as the settings knowledge-base route: there is
            no knowledge entity in the permission map, and whoever wires a
            channel up is allowed to see what its agent quotes. Write access is
            the server's call on every endpoint.
          */}
          <Route
            path={relativePath(PATHS.KNOWLEDGE_BASE)}
            element={
              <HasPermission
                action='view'
                mechanism='route'
                entity={entities.integration}
              >
                <KnowledgeBasePage />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.KNOWLEDGE_BASE_DETAIL())}
            element={
              <HasPermission
                action='view'
                mechanism='route'
                entity={entities.integration}
              >
                <KnowledgeBasePage />
              </HasPermission>
            }
          />
          {/*
            Omni-rail destinations share ConversationsLayout (system bar +
            rail). Each tab is its own child route — adding a tab is a new
            path, not a branch inside the inbox page.
          */}
          <Route element={<ConversationsLayout />}>
            <Route
              path={relativePath(PATHS.CONVERSATIONS_KNOWLEDGE_BASE)}
              element={<KnowledgeBaseWorkspace />}
            />
            <Route
              path={relativePath(PATHS.CONVERSATIONS_KNOWLEDGE_BASE_DETAIL())}
              element={<KnowledgeBaseWorkspace />}
            />
            <Route
              path={relativePath(PATHS.CONVERSATIONS_AGENTS)}
              element={<AgentsWorkspace />}
            />
            <Route
              path={relativePath(PATHS.CONVERSATIONS_AGENTS_DETAIL())}
              element={<AgentsWorkspace />}
            />
            {/* Same `integration` gate as the contact detail and templates
                routes below — see the note there. */}
            <Route
              path={relativePath(PATHS.CONVERSATIONS_CONTACTS)}
              element={
                <HasPermission
                  action='view'
                  mechanism='route'
                  entity={entities.integration}
                >
                  <ContactsWorkspace />
                </HasPermission>
              }
            />
            <Route
              path={relativePath(PATHS.CONVERSATIONS_AUTOMATION)}
              element={
                <HasPermission
                  action='view'
                  mechanism='route'
                  entity={entities.commentAutomation}
                >
                  <AutomationsWorkspace />
                </HasPermission>
              }
            />
            <Route
              path={relativePath(PATHS.CONVERSATIONS_AUTOMATION_ACTIVITY())}
              element={
                <HasPermission
                  action='view'
                  mechanism='route'
                  entity={[
                    entities.commentAutomation,
                    entities.commentAutomationRun,
                  ]}
                >
                  <AutomationActivityPage />
                </HasPermission>
              }
            />
            <Route
              path={relativePath(PATHS.CONVERSATIONS_ONBOARDING)}
              element={<ConversationsOnboarding />}
            />
            <Route
              path={relativePath(PATHS.CONVERSATIONS_CHANNELS)}
              element={<ConversationsChannels />}
            />
            <Route
              path={relativePath(PATHS.CONVERSATIONS)}
              element={<ConversationsPage />}
            />
            <Route
              path={`${relativePath(PATHS.CONVERSATIONS)}/:conversationId`}
              element={<ConversationsPage />}
            />
          </Route>
          {/*
            Like the knowledge-base settings route above, contacts have no
            entity of their own in the permission map — the contact book is
            fed by the same inbox channels the `integration` entity gates, so
            the same gate applies to the list, the detail view and the
            message templates the inbox sends from. The list itself now lives
            in the Conversations shell; old links to it redirect there.
          */}
          <Route
            path={relativePath(PATHS.CONTACTS)}
            element={
              <Navigate
                to={createRoute(PATHS.CONVERSATIONS_CONTACTS)}
                replace
              />
            }
          />
          <Route
            path={relativePath(PATHS.CONTACT_DETAIL())}
            element={
              <HasPermission
                action='view'
                mechanism='route'
                entity={entities.integration}
              >
                <ContactDetailPage />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.MESSAGE_TEMPLATES)}
            element={
              <HasPermission
                action='view'
                mechanism='route'
                entity={entities.integration}
              >
                <MessageTemplatesPage />
              </HasPermission>
            }
          />
          {/* The list now lives in the Conversations shell: old links to it
              redirect there, and old new, detail and edit links open its
              drawers. */}
          <Route
            path={relativePath(PATHS.AUTOMATIONS)}
            element={
              <Navigate
                to={createRoute(PATHS.CONVERSATIONS_AUTOMATION)}
                replace
              />
            }
          />
          <Route
            path={relativePath(PATHS.AUTOMATION_NEW)}
            element={
              <HasPermission
                action='add'
                mechanism='route'
                entity={entities.commentAutomation}
              >
                <AutomationNewRedirect />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.AUTOMATION_EDIT())}
            element={
              <HasPermission
                action='change'
                mechanism='route'
                entity={entities.commentAutomation}
              >
                <AutomationEditRedirect />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PATHS.AUTOMATION_DETAIL())}
            element={
              <HasPermission
                action='view'
                mechanism='route'
                entity={entities.commentAutomation}
              >
                <AutomationDetailRedirect />
              </HasPermission>
            }
          />
          <Route
            path={relativePath(PUBLIC_PATH.UNAUTHORIZED)}
            element={<Unauthorized />}
          />
        </Route>

        <Route
          path='*'
          element={
            <AuthGuard shouldRedirectToLogin={false}>
              <NotFound />
            </AuthGuard>
          }
        />
      </Route>
    </Routes>
  );
};
